<template>
  <div class="maintainance-standard-item-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('maintainanceStandardItem.page.title')"
      :subtitle="$t('maintainanceStandardItem.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="_goBack">
          {{ $t('maintainanceStandardItem.back') }}
        </el-button>
        <el-button type="primary" icon="el-icon-plus" @click="_openAddMaintainanceStandardItemModal">
          {{ $t('maintainanceStandardItem.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="_listMaintainanceStandardItems">
        <template #left>
          <span class="ds-panel__title">{{ $t('maintainanceStandardItem.title') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="maintainanceStandardItemInfo.items" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border
        :empty-description="$t('maintainanceStandardItem.page.empty')" @retry="_listMaintainanceStandardItems">
        <el-table-column prop="itemTitle" :label="$t('maintainanceStandardItem.item')" align="left" min-width="160"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.itemTitle || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="itemId" :label="$t('maintainanceStandardItem.id')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.itemId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('maintainanceStandardItem.operation')" align="center" width="100" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" class="ds-btn-danger"
              @click="_openDeleteMaintainanceStandardItemModel(scope.row)">
              {{ $t('maintainanceStandardItem.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange">
        <template #summary>
          <span class="ds-tip-text">{{ $t('maintainanceStandardItem.page.tip') }}</span>
        </template>
      </ds-pagination-bar>
    </div>

    <choose-maintainance-standard-item ref="chooseModal" @success="_listMaintainanceStandardItems"></choose-maintainance-standard-item>
    <delete-maintainance-standard-item ref="deleteModal" @success="_listMaintainanceStandardItems"></delete-maintainance-standard-item>
  </div>
</template>

<script>
import { listMaintainanceStandardItem } from '@/api/inspection/maintainanceStandardItemApi'
import ChooseMaintainanceStandardItem from '@/components/inspection/chooseMaintainanceStandardItem'
import DeleteMaintainanceStandardItem from '@/components/inspection/deleteMaintainanceStandardItem'
import { getCommunityId } from '@/api/community/communityApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'MaintainanceStandardItemList',
  components: {
    ChooseMaintainanceStandardItem,
    DeleteMaintainanceStandardItem,
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      maintainanceStandardItemInfo: {
        items: [],
        standardId: ''
      },
      pagination: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.maintainanceStandardItemInfo.standardId = this.$route.query.standardId
    this._listMaintainanceStandardItems()
  },
  methods: {
    async _listMaintainanceStandardItems() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const params = {
          page: this.pagination.current,
          row: this.pagination.size,
          communityId: getCommunityId(),
          standardId: this.maintainanceStandardItemInfo.standardId
        }
        const { data, records } = await listMaintainanceStandardItem(params)
        this.maintainanceStandardItemInfo.items = Array.isArray(data) ? data : []
        this.pagination.total = records || 0
      } catch (error) {
        console.error('获取保养标准检查项失败:', error)
        this.hasError = true
        this.errorMessage = this.$t('maintainanceStandardItem.page.loadFailed')
        this.$message.error(this.$t('maintainanceStandardItem.fetchError'))
      } finally {
        this.loading = false
      }
    },
    _openAddMaintainanceStandardItemModal() {
      this.$refs.chooseModal.open({
        standardId: this.maintainanceStandardItemInfo.standardId
      })
    },
    _openDeleteMaintainanceStandardItemModel(item) {
      this.$refs.deleteModal.open({
        ...item,
        standardId: this.maintainanceStandardItemInfo.standardId
      })
    },
    _goBack() {
      this.$router.go(-1)
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this._listMaintainanceStandardItems()
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this._listMaintainanceStandardItems()
    }
  }
}
</script>

<style lang="scss" scoped>
.maintainance-standard-item-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
